import { date, index, jsonb, pgTable, text, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/**
 * Historical cover letters the user has sent out — imported as reference
 * material, not as career facts. Distinct from `cover_letters` (generated
 * outputs from the platform).
 *
 * Why a separate table:
 *   - Cover letters are narrative summaries; extracting roles/achievements
 *     from them produces noise.
 *   - The interesting signal is voice patterns, "why this company" framing,
 *     and — critically — whether the application led to a callback. Those
 *     teach the rubric what worked vs. what didn't FOR THIS USER.
 */
export const coverLetterArchives = pgTable(
  'cover_letter_archives',
  {
    id: id(),
    userId: text('user_id').notNull(),
    rawText: text('raw_text').notNull(),
    targetCompany: varchar('target_company', { length: 200 }),
    targetRole: varchar('target_role', { length: 200 }),
    sentOn: date('sent_on'),
    /**
     * Outcome of the application this letter was attached to.
     * 'pending' — still in flight or unknown
     * 'no_callback' — silence / generic rejection
     * 'callback' — recruiter or hiring-manager reach-out
     * 'screen' — phone screen reached
     * 'onsite' — interview loop reached
     * 'offer' — offer received
     */
    outcome: varchar('outcome', { length: 16 }).default('pending').notNull(),
    /**
     * Claude-extracted narrative themes (≤6 short tags).
     * e.g., ["healthcare-architecture", "ai-platform", "founder-energy"]
     */
    themes: jsonb('themes').$type<string[]>(),
    /**
     * Sentence-level voice samples Claude flagged as characteristic of how
     * the user writes — feeds the tone presets for future generations.
     */
    voiceSamples: jsonb('voice_samples').$type<string[]>(),
    /**
     * Why-this-company framing extracted as a single short paragraph, if
     * present. Useful for spotting reusable hooks vs over-used clichés.
     */
    whyCompany: text('why_company'),
    notes: text('notes'),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('cover_letter_archives_user_idx').on(t.userId),
    outcomeIdx: index('cover_letter_archives_outcome_idx').on(t.outcome),
  }),
);
